import { useEffect, useState } from 'react';

import { t } from '../i18n/langue';

/**
 * Depuis combien de temps cette donnée a-t-elle été reçue — #24, #27, #86.
 *
 * ## La contrepartie de la rétention longue
 *
 * #86 fait passer le cache mobile de 5 minutes à une heure : tant que l'app vit,
 * ce qui a été lu reste lisible hors ligne. Le prix en est **non négociable** :
 * la donnée périmée doit **dire son âge**.
 *
 * Sans ça on retombe sur le « zéro plausible et faux » que #27 a refusé — une
 * conversation de tout à l'heure présentée comme l'état actuel, sans rien qui
 * distingue « il n'y a pas de nouveau message » de « je ne sais pas s'il y en a ».
 *
 * Le bandeau ne suffit pas : il disparaît dès le retour du réseau, là où
 * l'affichage peut rester périmé encore un moment.
 *
 * ## Pourquoi un seuil
 *
 * En dessous, l'âge est du bruit : personne n'a besoin qu'on lui dise « il y a
 * 4 secondes ». Il n'apparaît que quand il commence à vouloir dire quelque chose.
 */

/** En dessous, on ne dit rien : l'âge n'informe pas encore. */
const SEUIL_MS = 60_000;

/** Rafraîchi à la minute — la précision au-delà ne sert à rien. */
const PERIODE_MS = 30_000;

export function useAgeDeLaDonnee(receptionMs: number | undefined): string | null {
    const [maintenant, setMaintenant] = useState(() => Date.now());

    useEffect(() => {
        const minuteur = setInterval(() => setMaintenant(Date.now()), PERIODE_MS);

        return () => clearInterval(minuteur);
    }, []);

    if (!receptionMs) return null;

    const age = maintenant - receptionMs;
    if (age < SEUIL_MS) return null;

    const minutes = Math.floor(age / 60_000);
    if (minutes < 60) return t('composants.age.minutes', { minutes });

    const heures = Math.floor(minutes / 60);

    return t('composants.age.heures', { heures });
}
